import { Fragment, useState } from "react";
import ConfirmationModal from "../../../components/ui/popup/ConfirmationModal";

function AddressesPreview({ 
  addresses, 
  onEdit, 
  onDelete,
  showActions = true, 
  showEdit = true, // Control edit button visibility
  showDelete = true, // Control delete button visibility
  showDates = true,
  formatTitle = null
}) {
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  
  // Safety check to ensure addresses is an array
  if (!Array.isArray(addresses)) {
    return <div>No addresses to display</div>;
  }

  const capitalizeValue = (value) => {
    if (!value || typeof value !== 'string') return value || '';
    return value.split('_').map(word => word.toUpperCase()).join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${month}/${year}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatAddressTitle = (index, address) => {
    if (formatTitle && typeof formatTitle === 'function') {
      return formatTitle(address, index);
    }
    
    // If address has date range, show it in the title
    if (address && address.fromDate && address.toDate) {
      const fromDate = formatDate(address.fromDate);
      const toDate = formatDate(address.toDate);
      if (fromDate && toDate) {
        return `${fromDate} - ${toDate}`;
      }
    }
    
    return `Address ${index + 1}`;
  };

  const formatAddressString = (addr) => {
    const parts = [];
    
    if (addr.address) {
      parts.push(capitalizeValue(addr.address));
    }
    
    if (addr.suiteAddress) {
      parts.push(capitalizeValue(addr.suiteAddress));
    }
    
    if (addr.city) {
      parts.push(capitalizeValue(addr.city));
    }
    
    if (addr.stateAddress) {
      parts.push(capitalizeValue(addr.stateAddress));
    }
    
    if (addr.zipCode) {
      parts.push(capitalizeValue(addr.zipCode));
    }
    
    if (addr.country) {
      parts.push(capitalizeValue(addr.country));
    } else {
      parts.push("United States");
    }
    
    return parts.join(", ");
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex !== null && onDelete) {
      onDelete(deleteIndex);
      setDeleteIndex(null);
    }
    setShowDeleteConfirmation(false);
  };

  const handleCancelDelete = () => {
    setDeleteIndex(null);
    setShowDeleteConfirmation(false);
  };

  const addressToDelete = deleteIndex !== null ? addresses[deleteIndex] : null;
  const addressString = addressToDelete ? formatAddressString(addressToDelete) : "";

  return (
    <Fragment>
      <div className="addresspreview-container">
        {addresses.map((addr, index) => (
          <div key={index} className="addresspreview-item">
            <div className="addresspreview-header">
              {formatAddressTitle(index, addr) && (
                <h4 className="addresspreview-title">{formatAddressTitle(index, addr)}</h4>
              )}
              {showActions && (
                <div className="addresspreview-actions">
                  {showEdit && onEdit && (
                    <button
                      type="button"
                      className="addresspreview-action-btn addresspreview-action-btn-edit"
                      onClick={() => onEdit(index)}
                      aria-label="Edit address"
                    >
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/icons/basic_edit_blue.svg`} 
                        alt="Edit" 
                        className="addresspreview-icon addresspreview-icon-default"
                      />
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/icons/basic_edit_white.svg`} 
                        alt="Edit" 
                        className="addresspreview-icon addresspreview-icon-hover"
                      />
                    </button>
                  )}
                  {showDelete && onDelete && (
                    <button
                      type="button"
                      className="addresspreview-action-btn addresspreview-action-btn-delete"
                      onClick={() => handleDeleteClick(index)}
                      aria-label="Delete address"
                    >
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/icons/delete_red.svg`} 
                        alt="Delete" 
                        className="addresspreview-icon addresspreview-icon-default"
                      />
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/icons/delete_white.svg`} 
                        alt="Delete" 
                        className="addresspreview-icon addresspreview-icon-hover"
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="addresspreview-content">
              <span className="addresspreview-text">{formatAddressString(addr)}</span>
            </div>
          </div>
        ))}
      </div>
      {showDelete && onDelete && (
        <ConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Address"
          message={`Are you sure you want to delete this address? ${addressString ? `\n\n${addressString}` : ""}\n\nThis action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="primary"
          cancelButtonClass="secondary"
        />
      )}
    </Fragment>
  );
}

export default AddressesPreview;
